import React from 'react';
import { AdminCrudShell } from '@/components/admin/AdminCrudShell';
import { Badge } from '@/components/ui/badge';

// Tipo editorial del contenido (oculto el técnico "video_type" en este form).
const CONTENT_KIND_OPTS = [
  { value: 'highlights', label: 'Highlights' },
  { value: 'full_match', label: 'Partido completo' },
  { value: 'summary', label: 'Resumen' },
  { value: 'interview', label: 'Entrevista' },
  { value: 'statistics', label: 'Estadísticas' },
  { value: 'other', label: 'Otro' },
];

// Fuente técnica de reproducción.
const SOURCE_OPTS = [
  { value: 'mp4', label: 'URL MP4' },
  { value: 'hls', label: 'URL HLS (.m3u8)' },
  { value: 'embed', label: 'Embed (YouTube / Vimeo / Dailymotion / Bunny)' },
];

const STATUS_OPTS = [
  { value: 'draft', label: 'Borrador' },
  { value: 'published', label: 'Publicado' },
  { value: 'unavailable', label: 'Desactivado' },
];

// Versión SIMPLIFICADA visible (Entrega 1).
// Internamente, "Premium" mapea a access_type=premium (platform OR club-owner).
// "Pago individual" mapea a pay_per_content (Entrega 2 implementará el checkout).
const ACCESS_OPTS = [
  { value: 'free', label: 'Gratis' },
  { value: 'premium', label: 'Incluido en Premium' },
  { value: 'pay_per_content', label: 'Pago individual' },
];

const FILTER_STATUS = [{ value: '', label: 'Todos' }, ...STATUS_OPTS];
const FILTER_KIND = [{ value: '', label: 'Todos' }, ...CONTENT_KIND_OPTS];

export default function AdminVideos() {
  return (
    <AdminCrudShell
      title="Contenidos"
      subtitle="Vista transversal de auditoría. Gestión principal: Partidos → ficha → pestaña Contenidos (Entrega 2)."
      entity="videos"
      testIdPrefix="admin-videos"
      addLabel="Nuevo contenido"
      searchPlaceholder="Buscar por título…"
      filters={[
        { name: 'status', label: 'Estado', options: FILTER_STATUS },
        { name: 'content_kind', label: 'Tipo', options: FILTER_KIND },
      ]}
      initialForm={{
        event_id: '',
        title: '',
        description: '',
        content_kind: 'highlights',
        video_type: 'mp4',
        video_url: '', hls_url: '', embed_url: '',
        thumbnail_url: '',
        access_type: 'free',
        price_cents: '',
        currency: 'EUR',
        status: 'draft',
        is_primary: false,
        sort_order: 0,
      }}
      columns={[
        { key: 'title', label: 'Título' },
        {
          key: 'content_kind', label: 'Tipo',
          render: (r) => {
            const opt = CONTENT_KIND_OPTS.find((o) => o.value === (r.content_kind || 'highlights'));
            return <Badge variant="secondary" className="uppercase tracking-wider text-[10px]">{opt?.label || r.content_kind}</Badge>;
          },
        },
        {
          key: 'access_type', label: 'Acceso',
          render: (r) => {
            // Mapeo visible:
            const map = {
              free: 'Gratis',
              premium: 'Premium',
              premium_platform: 'Premium',
              premium_club: 'Premium',
              pay_per_content: 'Pago individual',
              pay_per_event: 'Pago individual',
              inherit_event: 'Heredado',
            };
            return (
              <Badge className="bg-[var(--ns-bg-2)] text-[var(--ns-text-muted)] border-[var(--ns-border)] uppercase text-[10px]">
                {map[r.access_type] || r.access_type}
              </Badge>
            );
          },
        },
        {
          key: 'status', label: 'Estado',
          render: (r) => (
            <Badge className={`text-[10px] uppercase ${r.status === 'published' || r.status === 'replay_available' ? 'bg-[var(--ns-gold)]/20 text-[var(--ns-gold-700)]' : 'bg-[var(--ns-bg-2)] text-[var(--ns-text-muted)]'}`}>
              {r.status === 'replay_available' ? 'Publicado' : (STATUS_OPTS.find((o) => o.value === r.status)?.label || r.status)}
            </Badge>
          ),
        },
      ]}
      formFields={[
        {
          name: 'event_id', label: 'Partido (UUID provisional)', required: true,
          placeholder: 'UUID del partido',
          lowercase: false,
          hint: 'En Entrega 2 este campo desaparecerá: los contenidos se crearán desde la ficha del partido.',
        },
        { name: 'title', label: 'Título del contenido', required: true, placeholder: 'Highlights del partido, entrevista al entrenador…' },
        { name: 'content_kind', label: 'Tipo', type: 'select', required: true, options: CONTENT_KIND_OPTS },
        { name: 'description', label: 'Descripción', type: 'textarea' },
        { name: 'video_type', label: 'Fuente de vídeo', type: 'select', required: true, options: SOURCE_OPTS,
          hint: 'Selecciona el tipo y luego rellena ÚNICAMENTE el campo de URL correspondiente.' },
        { name: 'video_url', label: 'URL MP4 (sólo si fuente = URL MP4)', placeholder: 'https://cdn.example.com/video.mp4', lowercase: false },
        { name: 'hls_url', label: 'URL HLS .m3u8 (sólo si fuente = URL HLS)', placeholder: 'https://streaming.example.com/playlist.m3u8', lowercase: false },
        { name: 'embed_url', label: 'URL embed (sólo si fuente = Embed)', placeholder: 'https://iframe.mediadelivery.net/embed/... o https://www.youtube-nocookie.com/embed/...', lowercase: false, hint: 'Hosts admitidos: YouTube, Vimeo, Dailymotion, Bunny Stream/CDN (incluye subdominios *.mediadelivery.net y *.b-cdn.net).' },
        { name: 'thumbnail_url', label: 'Imagen de portada (URL)', lowercase: false },
        { name: 'access_type', label: 'Acceso', type: 'select', required: true, options: ACCESS_OPTS },
        {
          name: 'price_cents', label: 'Precio',
          type: 'money',
          placeholder: '2,50',
          hint: 'Introduce el precio en euros (ej. 2,50). Solo aplicable si "Pago individual".',
          dependsOn: (v) => v.access_type === 'pay_per_content',
        },
        { name: 'status', label: 'Estado', type: 'select', required: true, options: STATUS_OPTS },
      ]}
    />
  );
}
